import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  FormControlLabel,
  Checkbox,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  IconButton,
  SvgIcon,
  Switch,
  useTheme,
  alpha
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { Chatbot, ChatbotFormData, User, KeywordResponse, ChatbotSettings } from '../../types/chatbot';
import { playSound } from '../../utils/soundUtils';

interface ChatbotDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => Promise<void>;
  formData: ChatbotFormData;
  setFormData: React.Dispatch<React.SetStateAction<ChatbotFormData>>;
  selectedChatbot: Chatbot | null;
  userProfile: User | null;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleOpenResponseDialog: (response?: KeywordResponse) => void;
  handleEditResponse: (response: KeywordResponse) => void;
  handleDeleteResponse: (id: string) => Promise<void>;
}

const ChatbotDialog: React.FC<ChatbotDialogProps> = ({
  open,
  onClose,
  onSubmit,
  formData,
  setFormData,
  selectedChatbot,
  userProfile,
  handleInputChange,
  handleOpenResponseDialog,
  handleEditResponse,
  handleDeleteResponse
}) => {
  const theme = useTheme();

  // Add a function to handle nested settings changes
  const handleSettingsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, path: string) => {
    const { value, type, checked } = e.target as HTMLInputElement; // Cast to HTMLInputElement to get checked property
    const pathParts = path.split('.');
    
    setFormData(prev => {
      // Create a deep copy of the settings to avoid mutating state directly
      const updatedSettings: ChatbotSettings = { ...prev.settings };
      
      if (pathParts.length === 2) {
        const [section, field] = pathParts;
        
        // Handle nested objects like aiSettings, soundSettings, etc.
        if (section === 'soundSettings') {
          updatedSettings.soundSettings = {
            ...updatedSettings.soundSettings,
            [field]: type === 'checkbox' ? checked : value
          };
        } else if (section === 'aiSettings') {
          updatedSettings.aiSettings = {
            ...updatedSettings.aiSettings,
            [field]: type === 'checkbox' ? checked : value
          };
        } else if (section === 'businessHours') {
          updatedSettings.businessHours = {
            ...updatedSettings.businessHours,
            [field]: type === 'checkbox' ? checked : value
          };
        } else if (section === 'defaultQuickReplies') {
          updatedSettings.defaultQuickReplies = {
            ...updatedSettings.defaultQuickReplies,
            [field]: type === 'checkbox' ? checked : value
          };
        }
      } else if (pathParts.length === 3) {
        const [section, subsection, field] = pathParts;
        
        // Handle deeper nested objects like businessHours.enabled
        if (section === 'businessHours') {
          updatedSettings.businessHours = {
            ...updatedSettings.businessHours,
            [subsection]: type === 'checkbox' ? checked : value
          };
        } else if (section === 'soundSettings') {
          updatedSettings.soundSettings = {
            ...updatedSettings.soundSettings,
            [subsection]: type === 'checkbox' ? checked : value
          };
        } else if (section === 'aiSettings') {
          updatedSettings.aiSettings = {
            ...updatedSettings.aiSettings,
            [subsection]: type === 'checkbox' ? checked : value
          };
        } else if (section === 'defaultQuickReplies') {
          updatedSettings.defaultQuickReplies = {
            ...updatedSettings.defaultQuickReplies,
            [subsection]: type === 'checkbox' ? checked : value
          };
        }
      }
      
      return {
        ...prev,
        settings: updatedSettings
      };
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {selectedChatbot ? 'Edit Chatbot' : 'Add Chatbot'}
      </DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={onSubmit} sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Welcome Message"
            name="welcomeMessage"
            value={formData.welcomeMessage}
            onChange={handleInputChange}
            margin="normal"
            required
            multiline
            rows={2}
          />
          <TextField
            fullWidth
            label="Primary Color"
            name="primaryColor"
            value={formData.primaryColor}
            onChange={handleInputChange}
            margin="normal"
            type="color"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            select
            label="Position"
            name="position"
            value={formData.position}
            onChange={handleInputChange}
            margin="normal"
          >
            <MenuItem value="bottom-right">Bottom Right</MenuItem>
            <MenuItem value="bottom-left">Bottom Left</MenuItem>
          </TextField>
          
          {/* Icon Type Selection */}
          <TextField
            fullWidth
            select
            label="Chat Button Icon"
            name="iconType"
            value={formData.iconType || 'default'}
            onChange={handleInputChange}
            margin="normal"
            helperText="Select the icon to display on the chat button"
          >
            <MenuItem value="default">Default Chat Bubble</MenuItem>
            <MenuItem value="agent">Customer Service Agent</MenuItem>
            <MenuItem value="emoji-smile">Smile Emoji</MenuItem>
            <MenuItem value="emoji-wave">Wave Emoji</MenuItem>
            <MenuItem value="question">Question Mark</MenuItem>
            <MenuItem value="support">Support Icon</MenuItem>
            <MenuItem value="custom">Custom Icon URL</MenuItem>
          </TextField>
          
          {formData.iconType === 'custom' && (
            <TextField
              fullWidth
              label="Custom Icon URL"
              name="customIconUrl"
              value={formData.customIconUrl}
              onChange={handleInputChange}
              margin="normal"
              helperText="Enter the URL of your custom icon (recommended size: 32x32px)"
            />
          )}
          
          <TextField
            fullWidth
            label="Profile Picture URL"
            name="profilePictureUrl"
            value={formData.profilePictureUrl}
            onChange={handleInputChange}
            margin="normal"
            helperText="Enter the URL of your profile picture (recommended size: 100x100px)"
          />
          
          {/* Icon Preview */}
          <Box sx={{ mt: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
            <Typography variant="subtitle2" gutterBottom>
              Widget Preview
            </Typography>
            
            {/* Load the selected font from Google Fonts for the preview */}
            <style>
              {`@import url('https://fonts.googleapis.com/css2?family=${formData.fontFamily?.replace(' ', '+')}:wght@400;500;700&display=swap');`}
            </style>
            
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: formData.widgetShape === 'bubble' ? '16px' : '50%',
                backgroundColor: formData.primaryColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.15)',
                position: 'relative',
                '&::after': formData.widgetShape === 'bubble' ? {
                  content: '""',
                  position: 'absolute',
                  bottom: '-8px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  borderLeft: '10px solid transparent',
                  borderRight: '10px solid transparent',
                  borderTop: `10px solid ${formData.primaryColor}`,
                  width: 0,
                  height: 0
                } : {}
              }}
            >
              {formData.iconType === 'default' && (
                <SvgIcon sx={{ color: 'white' }}>
                  <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
                </SvgIcon>
              )}
              {formData.iconType === 'agent' && (
                <SvgIcon sx={{ color: 'white' }}>
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </SvgIcon>
              )}
              {formData.iconType === 'emoji-smile' && (
                <SvgIcon sx={{ color: 'white' }}>
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z" />
                </SvgIcon>
              )}
              {formData.iconType === 'emoji-wave' && (
                <SvgIcon sx={{ color: 'white' }}>
                  <path d="M20 12c0-4.42-3.58-8-8-8s-8 3.58-8 8 3.58 8 8 8 8-3.58 8-8zm-8 7c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7zm1.46-5.47L11.41 15l-2.12-2.12-1.06 1.06L11.41 17l3.59-3.59-1.06-1.06zM13.88 11.88L16 9.76l-1.88-1.88-2.12 2.12 1.88 1.88zm-8.23.35l1.88 1.88 1.88-1.88-1.88-1.88-1.88 1.88z" />
                </SvgIcon>
              )}
              {formData.iconType === 'question' && (
                <SvgIcon sx={{ color: 'white' }}>
                  <path d="M11 18h2v-2h-2v2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z" />
                </SvgIcon>
              )}
              {formData.iconType === 'support' && (
                <SvgIcon sx={{ color: 'white' }}>
                  <path d="M19 2H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h4l3 3 3-3h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 16h-4.83l-.59.59L12 20.17l-1.59-1.59-.58-.58H5V4h14v14zm-8-3h2v2h-2zm1-8c1.1 0 2 .9 2 2 0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4S8 6.79 8 9h2c0-1.1.9-2 2-2z" />
                </SvgIcon>
              )}
              {formData.iconType === 'custom' && formData.customIconUrl && (
                <img src={formData.customIconUrl} alt="Custom Icon" style={{ width: 32, height: 32, objectFit: 'contain' }} />
              )}
            </Box>
            
            {/* Font Sample Preview */}
            <Box 
              sx={{ 
                mt: 4, 
                width: '100%', 
                maxWidth: 350,
                fontFamily: formData.fontFamily, 
                border: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.2) : '#e0e0e0'}`,
                borderRadius: 2,
                p: 2,
                bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
              }}
            >
              <Typography variant="subtitle2" gutterBottom sx={{ 
                fontFamily: formData.fontFamily, 
                fontWeight: 600,
                color: theme.palette.mode === 'dark' ? theme.palette.primary.main : 'inherit' 
              }}>
                Font Preview: {formData.fontFamily}
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                <Box 
                  sx={{ 
                    bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.grey[700], 0.5) : '#f0f0f0', 
                    p: 1.5, 
                    borderRadius: '12px', 
                    alignSelf: 'flex-start',
                    maxWidth: '80%',
                    fontFamily: formData.fontFamily,
                    color: theme.palette.mode === 'dark' ? theme.palette.text.primary : 'inherit'
                  }}
                >
                  Hello! How can I help you today?
                </Box>
                
                <Box 
                  sx={{ 
                    bgcolor: formData.primaryColor, 
                    color: 'white', 
                    p: 1.5, 
                    borderRadius: '12px', 
                    alignSelf: 'flex-end',
                    maxWidth: '80%',
                    fontFamily: formData.fontFamily
                  }}
                >
                  I'd like to know more about your services.
                </Box>
              </Box>
            </Box>
          </Box>
          
          {/* Widget Shape Selection */}
          <TextField
            fullWidth
            select
            label="Widget Shape"
            name="widgetShape"
            value={formData.widgetShape || 'circle'}
            onChange={handleInputChange}
            margin="normal"
            helperText="Select the shape of the chat widget button"
          >
            <MenuItem value="circle">Circle</MenuItem>
            <MenuItem value="bubble">Chat Bubble</MenuItem>
          </TextField>
          
          {/* Font Family Selection */}
          <TextField
            fullWidth
            select
            label="Font Family"
            name="fontFamily"
            value={formData.fontFamily || 'Roboto'}
            onChange={handleInputChange}
            margin="normal"
            helperText="Select a professional font for your chat widget"
          >
            <MenuItem value="Roboto">Roboto</MenuItem>
            <MenuItem value="Open Sans">Open Sans</MenuItem>
            <MenuItem value="Lato">Lato</MenuItem>
            <MenuItem value="Montserrat">Montserrat</MenuItem>
            <MenuItem value="Playfair Display">Playfair Display</MenuItem>
            <MenuItem value="Poppins">Poppins</MenuItem>
            <MenuItem value="Inter">Inter</MenuItem>
          </TextField>
          
          {/* Sound Settings */}
          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            Sound Settings
          </Typography>
          <Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1, mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.settings.soundSettings.enabled}
                  onChange={(e) => handleSettingsChange(e, 'soundSettings.enabled')}
                  name="settings.soundSettings.enabled"
                />
              }
              label="Enable sounds for end users by default"
            />
            
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Message Sent Sound
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                fullWidth
                select
                name="settings.soundSettings.messageSentSound"
                value={formData.settings.soundSettings.messageSentSound}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleSettingsChange(e, 'soundSettings.messageSentSound')}
                disabled={!formData.settings.soundSettings.enabled}
                margin="dense"
                variant="outlined"
                size="small"
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="newmessage">New Message</MenuItem>
                <MenuItem value="pop">Pop</MenuItem>
                <MenuItem value="hospitalbell">Hospital Bell</MenuItem>
                <MenuItem value="dingdingding">Ding Ding Ding</MenuItem>
                <MenuItem value="dududu">Dududu</MenuItem>
                <MenuItem value="notification">Notification</MenuItem>
                <MenuItem value="8bit">8-bit</MenuItem>
              </TextField>
              <IconButton 
                size="small" 
                onClick={() => playSound(formData.settings.soundSettings.messageSentSound)}
                disabled={!formData.settings.soundSettings.enabled || formData.settings.soundSettings.messageSentSound === 'none'}
                sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' }, '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' } }}
              >
                <PlayArrowIcon />
              </IconButton>
            </Box>
            
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Message Received Sound
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                fullWidth
                select
                name="settings.soundSettings.messageReceivedSound"
                value={formData.settings.soundSettings.messageReceivedSound}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleSettingsChange(e, 'soundSettings.messageReceivedSound')}
                disabled={!formData.settings.soundSettings.enabled}
                margin="dense"
                variant="outlined"
                size="small"
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="newmessage">New Message</MenuItem>
                <MenuItem value="pop">Pop</MenuItem>
                <MenuItem value="hospitalbell">Hospital Bell</MenuItem>
                <MenuItem value="dingdingding">Ding Ding Ding</MenuItem>
                <MenuItem value="dududu">Dududu</MenuItem>
                <MenuItem value="notification">Notification</MenuItem>
                <MenuItem value="8bit">8-bit</MenuItem>
              </TextField>
              <IconButton 
                size="small" 
                onClick={() => playSound(formData.settings.soundSettings.messageReceivedSound)}
                disabled={!formData.settings.soundSettings.enabled || formData.settings.soundSettings.messageReceivedSound === 'none'}
                sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' }, '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' } }}
              >
                <PlayArrowIcon />
              </IconButton>
            </Box>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          {/* File Upload Settings */}
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.settings.allowFileUpload}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  settings: {
                    ...prev.settings,
                    allowFileUpload: e.target.checked
                  }
                }))}
              />
            }
            label="Allow File Upload"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.settings.businessHours.enabled}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  settings: {
                    ...prev.settings,
                    businessHours: {
                      ...prev.settings.businessHours,
                      enabled: e.target.checked
                    }
                  }
                }))}
              />
            }
            label="Enable Business Hours"
          />
          {formData.settings.businessHours.enabled && (
            <>
              <TextField
                fullWidth
                label="Start Time"
                name="start"
                value={formData.settings.businessHours.start}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  settings: {
                    ...prev.settings,
                    businessHours: {
                      ...prev.settings.businessHours,
                      start: e.target.value
                    }
                  }
                }))}
                margin="normal"
                type="time"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="End Time"
                name="end"
                value={formData.settings.businessHours.end}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  settings: {
                    ...prev.settings,
                    businessHours: {
                      ...prev.settings.businessHours,
                      end: e.target.value
                    }
                  }
                }))}
                margin="normal"
                type="time"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Timezone"
                name="timezone"
                value={formData.settings.businessHours.timezone}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  settings: {
                    ...prev.settings,
                    businessHours: {
                      ...prev.settings.businessHours,
                      timezone: e.target.value
                    }
                  }
                }))}
                margin="normal"
              />
            </>
          )}
          
          {/* Keyword Responses Section */}
          {selectedChatbot && (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Keyword Responses</Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenResponseDialog()}
                  size="small"
                >
                  Add Response
                </Button>
              </Box>

              {selectedChatbot.keywordResponses && selectedChatbot.keywordResponses.length > 0 ? (
                <Grid container spacing={2}>
                  {selectedChatbot.keywordResponses.map((response: KeywordResponse) => (
                    <Grid item xs={12} key={response._id}>
                      <Card variant="outlined" sx={{ bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : undefined }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box>
                              <Typography variant="subtitle1" gutterBottom>
                                Keywords: {response.keywords.join(', ')}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                Response: {response.response}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                <Typography variant="caption" color="textSecondary">
                                  Priority: {response.priority}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  Status: {response.isActive ? 'Active' : 'Inactive'}
                                </Typography>
                              </Box>
                            </Box>
                            <Box>
                              <IconButton
                                size="small"
                                onClick={() => handleEditResponse(response)}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteResponse(response._id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography color="textSecondary">
                  No keyword responses configured yet.
                </Typography>
              )}
            </Box>
          )}

          {/* Default Quick Replies */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Default Quick Replies
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.settings.defaultQuickReplies.enabled}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      defaultQuickReplies: {
                        ...prev.settings.defaultQuickReplies,
                        enabled: e.target.checked
                      }
                    }
                  }))}
                />
              }
              label="Show default quick replies when no response matches"
            />
            {formData.settings.defaultQuickReplies.enabled && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  select
                  fullWidth
                  margin="normal"
                  label="Maximum quick replies to display"
                  value={formData.settings.defaultQuickReplies.maxDisplay || 4}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      defaultQuickReplies: {
                        ...prev.settings.defaultQuickReplies,
                        maxDisplay: parseInt(e.target.value)
                      }
                    }
                  }))}
                  helperText="Choose how many quick reply options to display (maximum 4)"
                >
                  <MenuItem value={1}>1 quick reply</MenuItem>
                  <MenuItem value={2}>2 quick replies</MenuItem>
                  <MenuItem value={3}>3 quick replies</MenuItem>
                  <MenuItem value={4}>4 quick replies</MenuItem>
                </TextField>
                {formData.settings.defaultQuickReplies.replies.map((reply, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      fullWidth
                      label={`Quick Reply ${index + 1}`}
                      value={reply}
                      onChange={(e) => {
                        const newReplies = [...formData.settings.defaultQuickReplies.replies];
                        newReplies[index] = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          settings: {
                            ...prev.settings,
                            defaultQuickReplies: {
                              ...prev.settings.defaultQuickReplies,
                              replies: newReplies
                            }
                          }
                        }));
                      }}
                    />
                    <IconButton
                      color="error"
                      onClick={() => {
                        const newReplies = formData.settings.defaultQuickReplies.replies.filter((_, i) => i !== index);
                        setFormData(prev => ({
                          ...prev,
                          settings: {
                            ...prev.settings,
                            defaultQuickReplies: {
                              ...prev.settings.defaultQuickReplies,
                              replies: newReplies
                            }
                          }
                        }));
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      defaultQuickReplies: {
                        ...prev.settings.defaultQuickReplies,
                        replies: [...prev.settings.defaultQuickReplies.replies, '']
                      }
                    }
                  }))}
                  sx={{ mt: 1 }}
                >
                  Add Quick Reply
                </Button>
              </Box>
            )}
          </Box>

          {/* AI Settings Section */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              AI Settings
            </Typography>
            <Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1, mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.settings.aiSettings.enabled}
                    onChange={(e) => handleSettingsChange(e, 'aiSettings.enabled')}
                    name="settings.aiSettings.enabled"
                  />
                }
                label="Enable AI-powered responses"
              />
              
              {formData.settings.aiSettings.enabled && (
                <>
                  <TextField
                    fullWidth
                    label="Temperature"
                    name="settings.aiSettings.temperature"
                    type="number"
                    value={formData.settings.aiSettings.temperature}
                    onChange={(e) => handleSettingsChange(e, 'aiSettings.temperature')}
                    margin="normal"
                    disabled={!formData.settings.aiSettings.enabled}
                    inputProps={{ min: 0, max: 1, step: 0.1 }}
                    helperText="0 = more deterministic, 1 = more creative (recommended: 0.5)"
                  />
                  
                  <TextField
                    fullWidth
                    label="Custom Instructions"
                    name="settings.aiSettings.customInstructions"
                    value={formData.settings.aiSettings.customInstructions}
                    onChange={(e) => handleSettingsChange(e, 'aiSettings.customInstructions')}
                    margin="normal"
                    multiline
                    rows={4}
                    disabled={!formData.settings.aiSettings.enabled}
                    helperText="Provide instructions for how the AI should respond to user queries"
                  />
                </>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onSubmit} variant="contained" color="primary">
          {selectedChatbot ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChatbotDialog; 